<template>
  <div class="pop-results-arc-component">
    <svg class="results-svg">
      <defs>
        <filter id="shadow">
          <feDropShadow
            dx="0"
            dy="0"
            stdDeviation="1"
            flood-color="black"
            flood-opacity="0.5"
          />
        </filter>
      </defs>
      <circle
        class="results-arc results-background"
        cx="150"
        cy="140"
        r="120"
        :stroke-dasharray="backgroundDashArray"
      />
      <circle
        v-if="percent > 0"
        class="results-arc results-foreground"
        cx="150"
        cy="140"
        r="120"
        :stroke-dasharray="resultsDashArray"
      />
      <circle
        class="results-arc results-marker outer"
        cx="150"
        cy="140"
        r="120"
        :stroke-dasharray="markerDashArray"
        :style="markerRotation"
        filter="url(#shadow)"
      />
      <circle
        class="results-arc results-marker inner"
        cx="150"
        cy="140"
        r="120"
        :stroke-dasharray="markerDashArray"
        :style="markerRotation"
      />
    </svg>
    <div class="results-details">
      <span class="percent-range">{{ percentRange }}</span>
      <span class="likelihood">{{ likelihood }}</span>
      <span class="last-adjustment">Last Adjustment: {{ lastAdjustmentDate }}</span>
    </div>
  </div>
</template>

<script>
import { format } from 'date-fns';
// Total px for arc
const TOTAL_DASH_ARRAY = 377;
const GAP = 15;
const SIXTY = 210;
const TWENTY = 69;

export default {
  components: { },
  props: {
    percent: {
      type: Number,
      default: 1,
    },
    resultsDate: {
      type: Date,
    },
  },
  computed: {
    backgroundDashArray() {
      // Breaks the stroke into a half circle made up of three segments, 60%, 20%, and 20%, with a gap between segements.
      return `${SIXTY}px ${GAP}px ${TWENTY}px ${GAP}px ${TWENTY}px ${TOTAL_DASH_ARRAY}px`;
    },
    lastAdjustmentDate() {
      return format((this.resultsDate || new Date()), 'MM/dd/yyyy');
    },
    likelihood() {
      if (this.percentDecimal < .6) {
        return 'Not Likely';
      } else if (this.percentDecimal >= .6 && this.percentDecimal < .8) {
        return 'Fairly Likely';
      } else if (this.percentDecimal >= .8) {
        return 'Very Likely';
      }
    },
    markerDashArray() {
      return `1px ${TOTAL_DASH_ARRAY * 2}px`;
    },
    markerRotation() {
      // Due to the transform on the svg, we need to start at 180 degrees.
      // This will add the % of the remaing 180 degrees in the circle and add it to the base 180.
      const rotation = 180 + (180 * this.percentDecimal);
      return { transform: `rotate(${rotation}deg)`};
    },
    percentDecimal() {
      return (this.percent || 1) / 100;
    },
    percentRange() {
      if (this.percentDecimal < .6) {
        return '0-59%';
      } else if (this.percentDecimal >= .6 && this.percentDecimal < .8) {
        return '60-79%';
      } else if (this.percentDecimal >= .8) {
        return '80-99%';
      }
    },
    resultsDashArray() {
      // The end result of this will return a valid string of dashes and gaps to represent the fill of the stroke based on a users percentage
      //
      // We will look at the total amount of fill and then interpolate that into the same dash array pattern as `backgroundDashArray`,
      // stopping when the total fill length as been met
      //
      // Finally, we will take that array of values, join it (if appropriate) by the gap size (GAP),
      // and use TOTAL_DASH_ARRAY * 2 to ensure that we are leaving a gap for the remainder of the circle stroke.
      const values = [];
      let totalPx = TOTAL_DASH_ARRAY * this.percentDecimal;
      let segments = [SIXTY, TWENTY, TWENTY];
      segments.forEach(segment => {
        if (totalPx > 0) {
          if (totalPx >= segment) {
            values.push(segment);
            totalPx -= segment;
            totalPx -= GAP;
          } else {
            values.push(totalPx);
            totalPx -= totalPx;
          }
        }
      });
      const dashArrayString = values.map(v => `${Math.round(v)}px`).join(` ${GAP}px `);
      return `${dashArrayString} ${TOTAL_DASH_ARRAY * 2}px`;
    },
  },
};
</script>

<style lang="scss" scoped>
.pop-results-arc-component {
  height: 100%;
  padding: 12px 0;

  .results-svg {
    margin: 0 auto;

    .results-arc {
      fill: transparent;
      stroke-width: 10px;
      stroke-linecap: round;
      transform-origin: 150px 140px;
      transform: rotate(-180deg);

      &.results-background {
        stroke: #bef7ff;
      }

      &.results-foreground {
        stroke: #00dbfb;
      }

      &.results-marker {
        &.outer {
          stroke-width: 18px;
          stroke: #ffffff;
        }

        &.inner {
          stroke-width: 10px;
          stroke: #00dbfb;
        }
      }
    }
  }

  .results-details {
    margin-top: -100px;
    text-align: center;

    .percent-range {
      display: block;
      font-size: rem-calc(12);
      font-weight: $semi-bold;
      font-style: italic;
      margin: 12px auto;
    }

    .likelihood {
      margin: 0 auto;
      max-width: rem-calc(175);
      @include charter-black;
      display: block;
      font-size: rem-calc(40);
      line-height: 1;
    }

    .last-adjustment {
      font-size: rem-calc(10);
      color: #767676;
      display: block;
      margin-top: 20px;
    }
  }
}
</style>
